// Media Center — responsive image manifest.
//
// One source of truth mapping each original CDN URL to the pre-generated
// WebP variants under `frontend/public/media/optimized/`.
// The MEDIA_IMAGES constants map (slug → { requestedWidth: actualWidth })
// is auto-generated by /app/scripts/optimize_media_images.py, so if a
// master's native resolution is smaller than a requested target width
// (e.g. an 853-wide master + 1200 target) the srcset descriptor here
// still reflects the file's real pixel width — which is what the
// browser needs to pick correctly.
//
// The original CDN URL remains the fallback `src`, so if the
// /media/optimized/ path is missing (e.g. a broken deploy) the
// browser still shows the full-res image.
//
// The lightbox intentionally keeps the ORIGINAL URL — press users
// should always download the full-resolution asset.

import { MEDIA_IMAGES } from "./mediaImagesManifest";

const OPT = "/media/optimized";

const SLUG_BY_URL = {
  "m1mwk51x_800eb07e-f7f4-4a04-8849-503b075d450f.JPG": "hero",
  "v0gvpc4d_7616beac-82ea-4101-8726-0f69611b9ceb.JPG": "vision",
  "hx0ng6o6_dc11166c-f39a-4182-bce2-225915dac6fa.JPG": "bio",
  "tzg70hsf_IMG_2504.PNG": "studio",
  "zumd9zgk_IMG_2801.PNG": "booth",
  "wggp4mf7_IMG_2802.PNG": "performance-mic",
  "z5nrssmi_83429244-F4F3-45FF-B65A-00733047C91B.jpeg": "behind-booth",
};

function slugFor(url) {
  if (!url) return null;
  const key = Object.keys(SLUG_BY_URL).find((k) => url.endsWith(k));
  return key ? SLUG_BY_URL[key] : null;
}

// Build the srcset entries for a slug, using the *actual* pixel width
// of each variant. Any duplicate widths (produced when a master is
// smaller than a target) are collapsed so we don't emit two lines with
// the same descriptor.
function buildSrcSet(slug) {
  const widths = MEDIA_IMAGES[slug];
  if (!widths) return null;
  const seen = new Set();
  const entries = [];
  for (const [target, actual] of Object.entries(widths)) {
    if (seen.has(actual)) continue;
    seen.add(actual);
    entries.push(`${OPT}/${slug}-${target}.webp ${actual}w`);
  }
  return entries.join(", ");
}

// Build a srcSet + sizes for a given original CDN URL.
// If the URL isn't in our optimized manifest, returns nulls so the caller
// can gracefully fall back to just the `src` attribute.
export function responsiveImage(url, sizes = "100vw") {
  const slug = slugFor(url);
  if (!slug) return { src: url, srcSet: null, sizes: null };
  const srcSet = buildSrcSet(slug);
  return { src: url, srcSet, sizes };
}

// Convenience preload descriptors for the hero image — reused by the
// StructuredData preload link. We emit `imagesrcset` + `imagesizes` on the
// preload so the browser fetches the variant that matches the viewport
// (rather than always the full-res original). Fallback `href` points at
// the largest variant so ancient browsers still get an optimised file.
export function heroPreloadAttrs() {
  const slug = "hero";
  const imagesrcset = buildSrcSet(slug);
  // Pick the largest variant as the non-responsive fallback.
  const widths = MEDIA_IMAGES[slug] || {};
  const largestTarget = Math.max(...Object.keys(widths).map(Number));
  return {
    imagesrcset,
    imagesizes: "100vw",
    href: `${OPT}/${slug}-${largestTarget}.webp`,
  };
}
